import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../constants';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import { PageList } from '../components/PageComponents';
import EmailModal from '../components/Modal/EmailModal';
import { formatBoolean, formatDateTime } from '../utils/appUtils';
import { useTokens } from '../context/TokenContext';

const AdminEmails = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [pageData, setPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const [isReload, setIsReload] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'title';
    const searchValue = params.get('searchValue') || '';

    setErrorMessage(null);
    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    setLoading(true);
    axios.get(`${API_URL}/emails`, {
      params: { limit, currentPage: page, searchBy, searchValue },
      headers: {
        'Authorization': `Bearer ${adminAuthToken}`,
      },
    })
      .then((response) => {
        setPageData({ data: response.data.data, dataCount: response.data.dataCount });
      })
      .catch(error => {
        console.error('Failed to fetch data:', error);
        setErrorMessage('Failed to load email records.');
      })
      .finally(() => {
        setLoading(false);
      });

    if (isReload) {
      setIsReload(false);
    }
  }, [search, isReload]);

  const handlePageDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this email?")) {
      const toastId = toast.loading('Deleting ...', { autoClose: false });
      try {
        await axios.delete(`${API_URL}/emails/${id}`, {
          headers: {
            'Authorization': `Bearer ${adminAuthToken}`,
          },
        });

        setPageData((prev) => ({
          ...prev,
          data: prev.data.filter((email) => email.id !== id),
        }));

        toast.update(toastId, {
          render: `Email deleted successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } catch (err) {
        toast.update(toastId, {
          render: 'Failed to delete email',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/email/list', search: params.toString() });
  };

  if (errorMessage) {
    return <div style={{ color: 'red' }}><p>{errorMessage}</p></div>;
  }

  return (
    <>
      <Head title="Email History" />
      {loading ? (
        <LoadingDashboardContent progress={progress} />
      ) : (
        <PageList
          itemType="emails"
          itemId="id"
          pageTitle="Email History"
          pageData={pageData.data}
          actionApplyKeyMap={{ Delete: 'delete' }}
          columns={['View', 'From', 'To', 'Status', 'SentAt']}
          filterBy={{ '--select--': '', Receipient: 'to', Status: 'status', DateRange: 'datetime' }}
          columnKeyMap={{
            'View': (item) => <EmailModal data={item} />,
            'From': 'from',
            'To': 'to',
            'Status': 'status',
            'SentAt': (item) => formatDateTime(item.sentAt),
          }}
          onAddNew={false}
          onView={false}
          onEdit={false}
          onDelete={handlePageDelete}
          totalRows={pageData.dataCount}
          updateUrlParams={updateUrlParams}
          isReload={setIsReload}
        />
      )}
    </>
  );
};

export default AdminEmails;
